import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, FileText, Calendar, Clock, CheckCircle, 
  AlertCircle, XCircle, ArrowUp, ArrowDown, Activity, 
  DollarSign, BarChart3, PieChart, TrendingDown, RefreshCw
} from 'lucide-react';
import CountUp from 'react-countup';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RePieChart, 
         Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import QuickActions from './QuickActions';
import ActivityFeed from './ActivityFeed';
import { reportsAPI, bookingsAPI } from '../services/api';
import { useTenantBranding } from '../context/TenantBrandingContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { labNameAr } = useTenantBranding();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { 
      title: 'إجمالي الحجوزات', 
      value: 0, 
      icon: FileText, 
      change: 0, 
      prefix: '',
      suffix: '',
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-500'
    },
    { 
      title: 'العملاء الجدد', 
      value: 0, 
      icon: Users, 
      change: 0,
      prefix: '',
      suffix: '',
      color: 'purple',
      bgColor: 'bg-purple-50',
      iconBg: 'bg-purple-500'
    },
    { 
      title: 'الإيرادات اليومية', 
      value: 0, 
      icon: DollarSign, 
      change: 0,
      prefix: '',
      suffix: ' ريال',
      color: 'green',
      bgColor: 'bg-green-50',
      iconBg: 'bg-green-500'
    },
    { 
      title: 'التحاليل المتوفرة', 
      value: 0, 
      icon: Activity, 
      change: 0,
      prefix: '',
      suffix: '',
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconBg: 'bg-orange-500'
    },
  ]);

  const [recentBookings, setRecentBookings] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Single optimized API call — all stats computed server-side
      const [dashRes, bookingsRes] = await Promise.all([
        reportsAPI.getMainDashboard(),
        bookingsAPI.getAll({ limit: 5 }) // only for recent bookings list
      ]);

      const d = dashRes.data?.data || {};
      const bookings = bookingsRes.data?.data?.items || bookingsRes.data?.data || [];

      const todayBookings = d.todayBookings || 0;
      const todayRevenue = d.todayRevenue || 0;
      const newCustomers = d.newCustomers || 0;

      // Update stats from server-computed data
      setStats([
        {
          title: 'إجمالي الحجوزات',
          value: d.totalBookings || 0,
          icon: FileText,
          change: todayBookings > 0 ? ((todayBookings / Math.max(d.totalBookings || 1, 1)) * 100) : 0,
          prefix: '',
          suffix: '',
          color: 'blue',
          bgColor: 'bg-blue-50',
          iconBg: 'bg-blue-500'
        },
        {
          title: 'إجمالي العملاء',
          value: d.totalCustomers || 0,
          icon: Users,
          change: newCustomers > 0 ? ((newCustomers / Math.max(d.totalCustomers || 1, 1)) * 100) : 0,
          prefix: '',
          suffix: '',
          color: 'purple',
          bgColor: 'bg-purple-50',
          iconBg: 'bg-purple-500'
        },
        {
          title: 'الإيرادات اليومية',
          value: todayRevenue,
          icon: DollarSign,
          change: todayBookings,
          prefix: '',
          suffix: ' ريال',
          color: 'green',
          bgColor: 'bg-green-50',
          iconBg: 'bg-green-500'
        },
        {
          title: 'التحاليل المتوفرة',
          value: d.totalTests || 0,
          icon: Activity,
          change: 0,
          prefix: '',
          suffix: '',
          color: 'orange',
          bgColor: 'bg-orange-50',
          iconBg: 'bg-orange-500'
        },
      ]);

      // Recent bookings from the lightweight call
      setRecentBookings(bookings.slice(0, 5));

      // Chart data — pre-computed by backend
      setChartData(d.chartData || []);

      // Pie data from server status counts
      const sc = d.statusCounts || {};
      const total = d.totalBookings || 1;
      setPieData([
        { name: 'عميل ويب غير مؤكد', value: Math.round(((sc.online_unconfirmed || 0) / total) * 100), color: '#f59e0b' },
        { name: 'جديد', value: Math.round(((sc.new || 0) / total) * 100), color: '#eab308' },
        { name: 'تم التجميع', value: Math.round(((sc.sample_collected || 0) / total) * 100), color: '#3b82f6' },
        { name: 'قيد المعالجة', value: Math.round(((sc.in_progress || 0) / total) * 100), color: '#8b5cf6' },
        { name: 'مكتمل', value: Math.round(((sc.completed || 0) / total) * 100), color: '#10b981' },
        { name: 'ملغي', value: Math.round(((sc.cancelled || 0) / total) * 100), color: '#ef4444' },
      ].filter(item => item.value > 0));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'online_unconfirmed': { label: 'عميل ويب غير مؤكد', icon: Clock, bg: 'bg-amber-100', text: 'text-amber-700', borderColor: 'border-amber-200' },
      'new': { label: 'جديد', icon: Clock, bg: 'bg-yellow-100', text: 'text-yellow-700', borderColor: 'border-yellow-200' },
      'sample_collected': { label: 'تم التجميع', icon: CheckCircle, bg: 'bg-blue-100', text: 'text-blue-700', borderColor: 'border-blue-200' },
      'in_progress': { label: 'قيد المعالجة', icon: RefreshCw, bg: 'bg-purple-100', text: 'text-purple-700', borderColor: 'border-purple-200' },
      'completed': { label: 'مكتمل', icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700', borderColor: 'border-green-200' },
      'cancelled': { label: 'ملغي', icon: XCircle, bg: 'bg-red-100', text: 'text-red-700', borderColor: 'border-red-200' },
    };
    const config = statusConfig[status] || statusConfig['new'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.borderColor}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getCustomerName = (customer) => {
    if (!customer) return '-';
    return customer.full_name || '-';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '-';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `قبل ${days} يوم`;
    if (hours > 0) return `قبل ${hours} ساعة`;
    if (minutes > 0) return `قبل ${minutes} دقيقة`;
    return 'الآن';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-6 flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6">
      {/* Page Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">لوحة التحكم الرئيسية</h1>
          <p className="text-gray-600 text-sm">مرحباً بك في نظام إدارة {labNameAr || 'المختبر'}</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={fetchDashboardData}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-500 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            تحديث
          </button>
          <button 
            onClick={() => navigate('/bookings')}
            className="hidden md:flex px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            عرض الحجوزات
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -2 }}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all stat-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${stat.iconBg} flex items-center justify-center text-white`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${
                  stat.change > 0 ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'
                }`}>
                  {stat.change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  <span className="text-xs font-semibold">{Math.abs(stat.change).toFixed(1)}%</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-2">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-900">
                {stat.prefix}
                <CountUp end={stat.value} duration={2} separator="," decimals={stat.suffix === '%' ? 1 : 0} />
                {stat.suffix}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Charts and Quick Actions Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">إحصائيات الأسبوع</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-600">الحجوزات</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm text-gray-600">الإيرادات</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
              <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} />
              <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  background: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="tests" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="revenue" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">توزيع الحجوزات</h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <RePieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#fff', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </RePieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {pieData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ background: item.color }} />
                    <div>
                      <span className="text-xs text-gray-600 block">{item.name}</span>
                      <span className="text-sm font-semibold text-gray-900">{item.value}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <PieChart className="w-12 h-12 mb-2" />
              <p className="text-sm">لا توجد بيانات</p>
            </div>
          )}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <QuickActions />
        </motion.div>
      </div>

      {/* Activity Feed and Recent Tests */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-1"
        >
          <ActivityFeed />
        </motion.div>
        
        {/* Recent Bookings Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">آخر الحجوزات</h3>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/bookings')}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              عرض جميع الحجوزات
            </motion.button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {recentBookings.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">العميل</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">رقم الحجز</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">المبلغ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">الوقت</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentBookings.map((booking) => (
                  <motion.tr
                    key={booking._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/bookings/${booking._id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {getCustomerName(booking.customer_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-mono">
                      {booking.booking_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {booking.net_amount?.toFixed(2)} ريال
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(booking.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimeAgo(booking.booking_date)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mb-2" />
              <p className="text-sm">لا توجد حجوزات حتى الآن</p>
              <button 
                onClick={() => navigate('/bookings/new')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                إنشاء حجز جديد
              </button>
            </div>
          )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
